import React, { useEffect, useCallback } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { Stage, Layer } from 'react-konva'
import {
  pictogramsAtom,
  pictogramsFamilyAtom,
  textSettingsAtom,
  customImagesFamilyAtom,
  isDevEnabledAtom,
  selectedPictogramsAtom,
} from 'atoms'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { useNavigate } from 'react-router-dom'
import StyledIconButton from './StyledIconButton'
import Caption from './Caption'
import Pictogram from './Pictogram'
import { pictoSizeAtom, textSizeAtom } from 'atoms'
import CardActions from './CardActions'
import CardCounter from './CardCounter'
import StyledFab from './StyledFab'
import Intersection from './Intersection'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { typeColorMapping } from 'constants'

import { Card, CardContent } from '@mui/material'

const PictogramCard = ({ uuid, showActions = true }) => {
  const [pictogram, setPictogram] = useAtom(pictogramsFamilyAtom(uuid))
  const setSelectedPictograms = useSetAtom(selectedPictogramsAtom)
  const setPictograms = useSetAtom(pictogramsAtom)
  const [customImage, setCustomImage] = useAtom(customImagesFamilyAtom(uuid))
  const isDevEnabled = useAtomValue(isDevEnabledAtom)
  const pictoSize = useAtomValue(pictoSizeAtom)
  const isMiscellaneous = pictogram.pictos?.some((picto) => picto?.type === 6)
  const {
    source: caption,
    pictos,
    wordType,
    tense,
    children,
    parent,
    currentIndex,
    imageAsText,
  } = pictogram

  const imageIds = React.useMemo(() => pictos || [], [pictos])
  const { position, font, capitalLetters } = useAtomValue(textSettingsAtom)
  const fontSize = useAtomValue(textSizeAtom)
  const [parentPictogram] = useAtom(pictogramsFamilyAtom(parent))
  const navigate = useNavigate()
  const type = parent ? 'child' : children.length ? 'parent' : 'none'
  const color = typeColorMapping[pictos[currentIndex]?.type] || 'lightgrey'

  // Move URL generation to a memoized function
  const generateImageUrl = useCallback(
    (currentImageId, options) => {
      const resolution = 500
      let option = ''

      if (wordType === 'verb') {
        if (tense === 'past') option = 'action-past_'
        else if (tense === 'future') option = 'action-future_'
      }

      if (wordType === 'noun' || wordType === 'adjective') {
        if (options.plural) option = 'plural_'
      }
      if (options.noColor) option += 'nocolor_'
      return `https://static.arasaac.org/pictograms/${currentImageId}/${currentImageId}_${option}${resolution}.png`
    },
    [tense, wordType]
  )

  // Use useEffect with ESLint disable comment
  // useEffect(() => {
  //   if (!imageIds || imageIds.length === 0) return
  //   const { id: currentImageId, plural, noColor } = imageIds[currentIndex]

  //   const newImageUrl = generateImageUrl(currentImageId, { plural, noColor })
  //   setImageURL(newImageUrl)
  // }, [imageIds, currentIndex, generateImageUrl])

  useEffect(() => {
    if (!imageIds || imageIds.length === 0) return
    const { id: currentImageId, plural, noColor } = imageIds[currentIndex]
    setPictogram((prevPictogram) => ({
      ...prevPictogram, // Mantenemos las demás propiedades intactas
      imageURL: generateImageUrl(currentImageId, { plural, noColor }),
    }))
  }, [currentIndex, generateImageUrl, imageIds, setPictogram])

  const handleNextImage = () => {
    setPictogram((prevPictogram) => ({
      ...prevPictogram, // Mantenemos las demás propiedades intactas
      currentIndex: (prevPictogram.currentIndex + 1) % imageIds.length,
    }))
  }

  const handlePictogramClick = () => {
    // add this pictogram to an array of uuids of pictograms using a new atom
    setSelectedPictograms((prevSelectedPictograms) => {
      // Add the pictogram to the selected array
      return [...prevSelectedPictograms, uuid]
    })
  }

  const handleShowChildren = () => {
    const children = pictogram.children // Parent pictogram's children

    // Use setPictograms to update the parent and children directly
    setPictograms((prevPictograms) => {
      // Only update the parent and its children
      const updatedPictograms = {}

      // Update the parent pictogram visibility to false
      updatedPictograms[pictogram.uuid] = {
        ...prevPictograms[pictogram.uuid],
        visible: false,
      }

      // Update each child's visibility to true
      children.forEach((uuid) => {
        updatedPictograms[uuid] = {
          ...prevPictograms[uuid],
          visible: true,
        }
      })

      // Return a new object by merging the previous state with the updates for parent and children
      return {
        ...prevPictograms,
        ...updatedPictograms, // Only update the entries that were changed
      }
    })
  }

  const handleVisibility = () => {
    console.log('handleVisibility', pictogram.imageAsText)
    setPictogram((prevPictogram) => ({
      ...prevPictogram, // Mantenemos las demás propiedades intactas
      imageAsText: !prevPictogram.imageAsText,
    }))
  }

  const handleShowParent = () => {
    const { uuid: parentUUID, children } = parentPictogram // Get the parent UUID

    // Use setPictograms to update the parent and children directly
    setPictograms((prevPictograms) => {
      // Only update the parent and its children
      const updatedPictograms = {}

      // Update the parent pictogram visibility to false
      updatedPictograms[parentUUID] = {
        ...prevPictograms[parentUUID],
        visible: true,
      }

      // Update each child's visibility to true
      children.forEach((uuid) => {
        updatedPictograms[uuid] = {
          ...prevPictograms[uuid],
          visible: false,
        }
      })

      // Return a new object by merging the previous state with the updates for parent and children
      return {
        ...prevPictograms,
        ...updatedPictograms, // Only update the entries that were changed
      }
    })
  }

  // Function to handle removing an item by UUID
  const handleRemove = (uuid) => {
    // if customImage, just remove it, don't remove pictogram
    if (customImage) {
      setCustomImage(null)
    } else {
      setPictograms((prevPictograms) => {
        // Only update the parent and its children
        const updatedPictograms = {}

        // Update the parent pictogram visibility to false
        updatedPictograms[uuid] = {
          ...prevPictograms[uuid],
          visible: false,
        }

        return {
          ...prevPictograms,
          ...updatedPictograms, // Only update the entries that were changed
        }
      })
    }
  }

  const handleEdit = () => {
    navigate(`/pictograms/${uuid}`)
  }

  const renderCaption = position !== 'none' && (
    <Caption
      caption={caption}
      color={color}
      font={font}
      type={type}
      id={uuid}
      capitalLetters={capitalLetters}
      fontSize={fontSize}
    />
  )

  const renderCardActions = showActions && (
    <CardActions sx={{ p: 0 }}>
      <StyledIconButton onClick={handleEdit}>
        <EditIcon sx={{ fontSize }} />
      </StyledIconButton>

      {isMiscellaneous && (
        <StyledIconButton onClick={handleVisibility}>
          {imageAsText ? (
            <VisibilityIcon sx={{ fontSize }} />
          ) : (
            <VisibilityOffIcon sx={{ fontSize }} />
          )}
        </StyledIconButton>
      )}
      {!isMiscellaneous && (
        <CardCounter
          imageIds={imageIds}
          currentIndex={currentIndex}
          fontSize={fontSize}
          onClick={handleNextImage}
        />
      )}
      <StyledIconButton onClick={() => handleRemove(uuid)}>
        <DeleteIcon sx={{ fontSize }} />
      </StyledIconButton>
    </CardActions>
  )

  const renderFab = () => {
    if (type === 'parent') {
      return (
        <StyledFab size="small" color="secondary" position={position}>
          <AddIcon onClick={handleShowChildren} />
        </StyledFab>
      )
    }

    return (
      <StyledFab size="small" color="secondary" position={position}>
        <RemoveIcon onClick={handleShowParent} />
      </StyledFab>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: pictoSize,
      }}
    >
      <Card
        style={{
          width: pictoSize,
          padding: '0px !important',
          textAlign: 'center',
          position: 'relative',
          overflow: 'visible',
          backgroundColor: 'white',
        }}
      >
        {type !== 'none' && renderFab()}
        {position === 'top' && renderCaption}

        {position === 'bottom' && renderCardActions}
        <CardContent
          onClick={handlePictogramClick}
          style={{
            padding: 0,
            width: pictoSize,
            height: 'pictoSize',
            display: 'flex',
            alignItems: 'center', // Vertical alignment
            justifyContent: 'center',
          }}
        >
          <Stage
            id={uuid}
            width={Number(pictoSize)}
            height={Number(pictoSize)}
            style={{ cursor: 'pointer' }}
          >
            <Layer>
              <Pictogram uuid={uuid} />
            </Layer>
          </Stage>
        </CardContent>

        {position === 'bottom' && renderCaption}
        {position !== 'bottom' && renderCardActions}
      </Card>
      {isDevEnabled && <Intersection uuid={uuid} currentIndex={currentIndex} />}
    </div>
  )
}

export default PictogramCard
